import { Extension } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import { Plugin, PluginKey, NodeSelection } from '@tiptap/pm/state';
import type { EditorState } from '@tiptap/pm/state';
import { ReplaceStep, ReplaceAroundStep, AddMarkStep, RemoveMarkStep, AttrStep } from '@tiptap/pm/transform';
import type { Step } from '@tiptap/pm/transform';
import type { NodeType, Node as PMNode, Schema } from '@tiptap/pm/model';
import { SELECTION_MARK_NAME } from '@modules/comments/constants';

import {
  TEMPLATE_FIELD_INLINE_NAME,
  TEMPLATE_FIELD_BLOCK_NAME,
  TEMPLATE_FIELD_DROPDOWN_NAME,
} from '../../doc-engine/templateFields';
import getTemplateCheckboxClickHandler from '../plugins/getTemplateCheckboxClickHandler';
import { isSystemTr } from '../plugins/shared';

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

type TFieldInfo = { depth: number; start: number };

const findFieldAncestor = (doc: PMNode, pos: number, fieldTypes: readonly NodeType[]): TFieldInfo | null => {
  const $pos = doc.resolve(pos);
  for (let d = $pos.depth; d >= 0; d--) {
    const nodeType = $pos.node(d).type;
    for (let t = 0; t < fieldTypes.length; t++) {
      if (nodeType === fieldTypes[t]) {
        return { depth: d, start: $pos.before(d) };
      }
    }
  }
  return null;
};

// Both ends must resolve inside the same field instance; a pure insertion (from === to) only checks `from`.
const isRangeInsideField = (doc: PMNode, from: number, to: number, fieldTypes: readonly NodeType[]): boolean => {
  const fromField = findFieldAncestor(doc, from, fieldTypes);
  if (!fromField) return false;
  if (from === to) return true;

  const toField = findFieldAncestor(doc, to, fieldTypes);
  if (!toField) return false;

  // Same field instance: both positions share the same ancestor start position
  return fromField.start === toField.start;
};

// True when [from, to] exactly wraps an atom field (a dropdown), which is the nodeAfter at `from`,
// not an ancestor - so isRangeInsideField can't see it.
const stepSpansFieldAtom = (doc: PMNode, from: number, to: number, fieldTypes: readonly NodeType[]): boolean => {
  const nodeAfter = doc.resolve(from).nodeAfter;
  return (
    nodeAfter !== null &&
    nodeAfter.isAtom &&
    from + nodeAfter.nodeSize === to &&
    fieldTypes.some((ft) => ft === nodeAfter.type)
  );
};

const isStepInsideField = (doc: PMNode, step: Step, fieldTypes: readonly NodeType[]): boolean => {
  if (step instanceof ReplaceStep || step instanceof ReplaceAroundStep) {
    return isRangeInsideField(doc, step.from, step.to, fieldTypes);
  }
  if (step instanceof AddMarkStep || step instanceof RemoveMarkStep) {
    if (isRangeInsideField(doc, step.from, step.to, fieldTypes)) return true;
    // Only a comment mark may span a whole atom field (dropdown); other marks stay locked out.
    return step.mark.type.name === SELECTION_MARK_NAME && stepSpansFieldAtom(doc, step.from, step.to, fieldTypes);
  }
  if (step instanceof AttrStep) {
    if (findFieldAncestor(doc, step.pos, fieldTypes) !== null) return true;
    // For atom field nodes (e.g. dropdown), the node AT the position is the field itself,
    // not an ancestor. Check if nodeAfter at the resolved position is a field type.
    const $pos = doc.resolve(step.pos);
    const nodeAtPos = $pos.nodeAfter;
    return nodeAtPos !== null && fieldTypes.some((ft) => ft === nodeAtPos.type);
  }
  // Unknown step type -> reject (fail-closed)
  return false;
};

/* ------------------------------------------------------------------ */
/* plugin                                                              */
/* ------------------------------------------------------------------ */

export const templateLockingPluginKey = new PluginKey<boolean>('template-locking');

export const createTemplateLockingPlugin = (schema: Schema, editor: Editor): Plugin<boolean> => {
  const fieldTypes = getFieldTypes(schema);

  return new Plugin<boolean>({
    key: templateLockingPluginKey,

    props: {
      handleDOMEvents: {
        click: getTemplateCheckboxClickHandler(
          (doc, position) => findFieldAncestor(doc, position, fieldTypes) !== null
        ),
      },
    },

    // Minimal state so isBoundDocument() can detect plugin presence
    state: {
      init() {
        return true;
      },
      apply(_tr, value) {
        return value;
      },
    },

    filterTransaction(tr) {
      // Selection/meta-only changes never edit content.
      if (!tr.docChanged) return true;

      // Plugin-generated transactions (appendTransaction, view dispatches) are trusted - the
      // accepted AI apply too: the backend validates a suggestion against the document kind.
      if (isSystemTr(tr)) return true;

      // View mode loads content via setContent; nothing to lock.
      if (!editor.isEditable) return true;

      // Every step must target content inside a field, or the whole transaction is rejected.
      for (let i = 0; i < tr.steps.length; i++) {
        const step = tr.steps[i];
        const doc = tr.docs[i];

        if (!isStepInsideField(doc, step, fieldTypes)) return false;
      }
      return true;
    },
  });
};

/* ------------------------------------------------------------------ */
/* exported utilities                                                   */
/* ------------------------------------------------------------------ */

const getFieldTypes = (schema: Schema): NodeType[] => {
  const types: NodeType[] = [];
  const fi = schema.nodes[TEMPLATE_FIELD_INLINE_NAME];
  const fc = schema.nodes[TEMPLATE_FIELD_BLOCK_NAME];
  const fd = schema.nodes[TEMPLATE_FIELD_DROPDOWN_NAME];
  if (fi) types.push(fi);
  if (fc) types.push(fc);
  if (fd) types.push(fd);
  return types;
};

/** Check if the current selection is entirely inside template field node(s). */
export const isSelectionInField = (state: EditorState): boolean => {
  const fieldTypes = getFieldTypes(state.schema);
  if (!fieldTypes.length) return false;

  // NodeSelection on an atom field node: the selection spans the node from outside,
  // so isRangeInsideField won't find a field ancestor. Check if the selected node itself is a field.
  // Only return early for actual field nodes - non-field NodeSelections (e.g. image inside block field)
  // must fall through to the range check below.
  if (state.selection instanceof NodeSelection) {
    const nodeSel = state.selection as NodeSelection;
    if (fieldTypes.some((ft) => ft === nodeSel.node.type)) return true;
  }

  const { from, to } = state.selection;
  return isRangeInsideField(state.doc, from, to, fieldTypes);
};

/**
 * True when the selection sits INSIDE the content of a template field (the field is an ancestor).
 * Unlike isSelectionInField, selecting a whole field node (e.g. a dropdown) is NOT included - there
 * is no field content there to anchor a comment in.
 */
export const isSelectionInsideFieldContent = (state: EditorState): boolean => {
  const fieldTypes = getFieldTypes(state.schema);
  if (!fieldTypes.length) return false;
  const { from, to } = state.selection;
  return isRangeInsideField(state.doc, from, to, fieldTypes);
};

/** Check if the template locking plugin is active (= document is bound). */
export const isBoundDocument = (state: EditorState): boolean => {
  return templateLockingPluginKey.getState(state) !== undefined;
};

/** Editing controls disable themselves on this predicate: the lock filters every edit outside
 *  field content, including replacing a whole selected field node. */
export const isLockedOutsideFields = (state: EditorState): boolean => {
  return isBoundDocument(state) && !isSelectionInsideFieldContent(state);
};

/* ------------------------------------------------------------------ */
/* extension                                                           */
/* ------------------------------------------------------------------ */

const TemplateLocking = Extension.create({
  name: 'template-locking',

  addProseMirrorPlugins() {
    return [createTemplateLockingPlugin(this.editor.schema, this.editor)];
  },
});

export default TemplateLocking;
